/* eslint-disable no-nested-ternary */
import { ChangeEvent } from 'react';
import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { OutboundProcess } from '@features/supplyChain/types';
import { useReactHookFormWithFieldArray } from '@hooks';
import { PalletCalculator } from '@components/PalletCalculator';
import { CreateOutboundShipmentNoteFormValues } from './types';
import { validationSchema, makeDefaultValues } from './form';
import { Datepicker } from '../../../tailwindComponents/Datepicker/Datepicker';
import { Input } from '../../../tailwindComponents/Input/Input';
import { Textarea } from '../../../tailwindComponents/Textarea/Textarea';
import { Button } from '../../../tailwindComponents/Button/Button';

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2' />
      <path d='M15 18H9' />
      <path d='M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14' />
      <circle cx='17' cy='18' r='2' />
      <circle cx='7' cy='18' r='2' />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z' />
      <circle cx='12' cy='10' r='3' />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='10' />
      <line x1='15' x2='9' y1='9' y2='15' />
      <line x1='9' x2='15' y1='9' y2='15' />
    </svg>
  );
}

interface CreateOutboundShipmentNoteFormProps {
  selectedOutboundProcess: OutboundProcess;
  onClose: () => void;
  onSubmit: (values: CreateOutboundShipmentNoteFormValues) => void;
  isDesktop: boolean;
  isTablet: boolean;
}

export function CreateOutboundShipmentNoteForm({
  selectedOutboundProcess,
  onClose,
  onSubmit,
  isDesktop,
  isTablet,
}: CreateOutboundShipmentNoteFormProps) {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    isSubmitting,
    isValid,
    fields: skusFields,
  } = useReactHookFormWithFieldArray<CreateOutboundShipmentNoteFormValues>({
    defaultValues: makeDefaultValues(selectedOutboundProcess),
    validationSchema,
    fieldArrayName: 'skus',
  });

  if (!selectedOutboundProcess || !selectedOutboundProcess.despatchNote) {
    return null;
  }

  const { despatchNote } = selectedOutboundProcess;
  const shipmentNoteNumber = despatchNote.despatchNoteNumber.replace(
    '-DN',
    '-SN'
  );

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(shipmentNoteNumber);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            Shipment note #{shipmentNoteNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label='Copy shipment note number'
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Recipient name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Recipient name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {despatchNote.customer?.name ?? despatchNote.customerName ?? 'N/A'}
          </span>
        </div>

        {/* Recipient address */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <MapPinIcon />
            <span className='text-xs font-medium'>Recipient address</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {despatchNote.customer?.fullAddress ?? 'N/A'}
          </span>
        </div>

        {/* Shipped with */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <TruckIcon />
            <span className='text-xs font-medium'>Shipped with</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>ABC Haulage</span>
        </div>

        {/* Required time of arrival */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Required ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {`${dayjs(despatchNote.requiredTimeOfArrival.dateTime)
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('D MMM YYYY')} · by ${dayjs(
              despatchNote.requiredTimeOfArrival.dateTime
            )
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('HH:mm')}`}
          </span>
        </div>
      </div>

      {/* Second row of metadata */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* ETA */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs(despatchNote.requiredTimeOfArrival.dateTime)
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .add(1, 'day')
              .format('D MMM YYYY · HH:mm')}
          </span>
        </div>

        {/* Shipment Date - Editable */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500 mb-1'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Shipment date</span>
          </div>
          <Controller
            name='shipmentDate'
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <Datepicker
                  name={field.name}
                  value={
                    field.value
                      ? dayjs(field.value).format('YYYY-MM-DD')
                      : dayjs().format('YYYY-MM-DD')
                  }
                  onChange={(e) => {
                    setValue('shipmentDate', dayjs(e.target.value).format(), {
                      shouldValidate: true,
                    });
                  }}
                  startDate={dayjs().format('YYYY-MM-DD')}
                  isInvalid={!!error}
                  validationMessage={error?.message}
                />
              );
            }}
          />
        </div>
      </div>

      {/* SKU Table */}
      <div className='px-6 py-4'>
        <h3 className='text-sm font-semibold text-gray-900 mb-4'>
          SKU / items
        </h3>

        {/* Table Header - Desktop only */}
        {isDesktop && (
          <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1fr_1.5fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
            <span className='text-xs font-semibold text-gray-500 text-center'>
              #
            </span>
            <span className='text-xs font-semibold text-gray-500'>SKU ID</span>
            <span className='text-xs font-semibold text-gray-500'>
              SKU name
            </span>
            <span className='text-xs font-semibold text-gray-500'>UoM</span>
            <span className='text-xs font-semibold text-gray-500'>
              Picked SKUs
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Despatched SKUs
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Despatched Pallets
            </span>
          </div>
        )}

        {/* SKU Rows */}
        <div className='flex flex-col gap-6'>
          {skusFields.map((skusField, index) => {
            const { pickedQuantity } = skusField;
            const despatchedQuantity = watch(
              `skus.${index}.despatchedQuantity`
            );

            const isDiscrepancy =
              despatchedQuantity < pickedQuantity ||
              despatchedQuantity > pickedQuantity;

            const hasPackagingInfo =
              despatchNote.skus.find((s) => s.skuId === skusField.skuId)
                ?.packagingDetails.packagingInfo?.amountOfSkusInLayer &&
              despatchNote.skus.find((s) => s.skuId === skusField.skuId)
                ?.packagingDetails.packagingInfo?.amountOfLayersInPallet;

            let amountOfSkusInLayer: number | undefined;
            let amountOfLayersInPallet: number | undefined;
            let packagingInfoType: string | undefined;
            let palletisingType: string | null | undefined;

            if (hasPackagingInfo) {
              amountOfSkusInLayer = despatchNote.skus.find(
                (s) => s.skuId === skusField.skuId
              )?.packagingDetails.packagingInfo?.amountOfSkusInLayer;

              amountOfLayersInPallet = despatchNote.skus.find(
                (s) => s.skuId === skusField.skuId
              )?.packagingDetails.packagingInfo?.amountOfLayersInPallet;

              packagingInfoType = despatchNote.skus.find(
                (s) => s.skuId === skusField.skuId
              )?.packagingDetails.packagingInfo?.type;
            } else {
              palletisingType = despatchNote.skus.find(
                (s) => s.skuId === skusField.skuId
              )?.packagingDetails.palletisingType;
            }

            return (
              <div
                key={`${skusField.id}-${skusField.skuIdentifier}`}
                className={`${
                  index !== skusFields.length - 1
                    ? 'pb-6 border-b border-gray-100'
                    : ''
                }`}
              >
                {isDesktop ? (
                  /* Desktop Layout */
                  <div className='flex flex-col gap-4'>
                    <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1fr_1.5fr] gap-4 items-start'>
                      {/* Index */}
                      <div className='flex justify-center'>
                        <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                          {index + 1}
                        </span>
                      </div>
                      {/* SKU ID */}
                      <span className='text-sm text-gray-900'>
                        {skusField.skuIdentifier}
                      </span>
                      {/* SKU name */}
                      <span className='text-sm text-gray-900'>
                        {skusField.name}
                      </span>
                      {/* UoM */}
                      <span className='text-sm text-gray-900'>
                        {skusField.name}
                      </span>
                      {/* Picked quantity */}
                      <span className='text-sm text-gray-900'>
                        {skusField.pickedQuantity}
                      </span>
                      {/* Despatched SKU Quantity */}
                      <Controller
                        name={`skus.${index}.despatchedQuantity`}
                        control={control}
                        render={({
                          field,
                          fieldState: { error, isTouched },
                        }) => (
                          <Input
                            name={field.name}
                            type='number'
                            value={field.value?.toString() ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const despatchedQuantityValue = parseInt(
                                e.target.value,
                                10
                              );

                              setValue(
                                `skus.${index}.despatchedQuantity`,
                                despatchedQuantityValue,
                                {
                                  shouldValidate: true,
                                }
                              );

                              if (despatchedQuantityValue === pickedQuantity) {
                                setValue(`skus.${index}.discrepancyReason`, '');
                              }
                            }}
                            isInvalid={Boolean(error && isTouched)}
                            validationMessage={error?.message}
                            sizeVariant='sm'
                          />
                        )}
                      />
                      {/* Despatched Pallet Quantity */}
                      <span className='text-sm text-gray-900 whitespace-nowrap'>
                        {hasPackagingInfo ? (
                          despatchedQuantity ? (
                            <PalletCalculator
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              palletName={packagingInfoType!}
                              skuQuantity={despatchedQuantity}
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              amountOfSkusInLayer={amountOfSkusInLayer!}
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              amountOfLayersInPallet={amountOfLayersInPallet!}
                            />
                          ) : (
                            <span>&nbsp;</span>
                          )
                        ) : (
                          palletisingType
                        )}
                      </span>
                    </div>

                    {/* Discrepancy reason row */}
                    {isDiscrepancy && (
                      <div className='grid grid-cols-[2.5rem_1fr] gap-4'>
                        <div />
                        <Controller
                          name={`skus.${index}.discrepancyReason`}
                          control={control}
                          render={({
                            field,
                            fieldState: { error, isTouched },
                          }) => (
                            <Textarea
                              name={field.name}
                              label='Discrepancy reason'
                              value={field.value ?? ''}
                              onChange={(
                                e: ChangeEvent<HTMLTextAreaElement>
                              ) => {
                                field.onChange(e.target.value);
                              }}
                              onBlur={field.onBlur}
                              isInvalid={Boolean(error && isTouched)}
                              validationMessage={error?.message}
                              placeholder='Explain the discrepancy...'
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  /* Mobile/Tablet Layout */
                  <div className='flex gap-3'>
                    {/* Index */}
                    <div className='flex-shrink-0'>
                      <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                        {index + 1}
                      </span>
                    </div>
                    {/* Details */}
                    <div className='flex-1 flex flex-col gap-4'>
                      <div
                        className={`grid gap-3 ${
                          isTablet ? 'grid-cols-2' : 'grid-cols-1'
                        }`}
                      >
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            SKU ID
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.skuIdentifier}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            SKU name
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.name}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            UoM
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.name}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Picked SKUs
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.pickedQuantity}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Despatched SKUs
                          </span>
                          <Controller
                            name={`skus.${index}.despatchedQuantity`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const despatchedQuantityValue = parseInt(
                                    e.target.value,
                                    10
                                  );

                                  setValue(
                                    `skus.${index}.despatchedQuantity`,
                                    despatchedQuantityValue,
                                    {
                                      shouldValidate: true,
                                    }
                                  );

                                  if (
                                    despatchedQuantityValue === pickedQuantity
                                  ) {
                                    setValue(
                                      `skus.${index}.discrepancyReason`,
                                      ''
                                    );
                                  }
                                }}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Despatched Pallets
                          </span>
                          <span className='text-sm text-gray-900'>
                            {hasPackagingInfo ? (
                              despatchedQuantity ? (
                                <PalletCalculator
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  palletName={packagingInfoType!}
                                  skuQuantity={despatchedQuantity}
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  amountOfSkusInLayer={amountOfSkusInLayer!}
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  amountOfLayersInPallet={
                                    amountOfLayersInPallet!
                                  }
                                />
                              ) : (
                                <span>&nbsp;</span>
                              )
                            ) : (
                              palletisingType
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Discrepancy reason */}
                      {isDiscrepancy && (
                        <Controller
                          name={`skus.${index}.discrepancyReason`}
                          control={control}
                          render={({
                            field,
                            fieldState: { error, isTouched },
                          }) => (
                            <Textarea
                              name={field.name}
                              label='Discrepancy reason'
                              value={field.value ?? ''}
                              onChange={(
                                e: ChangeEvent<HTMLTextAreaElement>
                              ) => {
                                field.onChange(e.target.value);
                              }}
                              onBlur={field.onBlur}
                              isInvalid={Boolean(error && isTouched)}
                              validationMessage={error?.message}
                              placeholder='Explain the discrepancy...'
                            />
                          )}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comment Section */}
      <div className='px-6 py-4 border-t border-gray-200'>
        <Controller
          name='comment'
          control={control}
          render={({ field, fieldState: { error, isTouched } }) => (
            <Textarea
              {...field}
              value={field.value ?? ''}
              label='Comment'
              optional
              isInvalid={Boolean(error && isTouched)}
              validationMessage={error?.message}
              placeholder='Type here...'
            />
          )}
        />
      </div>

      {/* Footer Actions */}
      <div
        className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
          isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
        }`}
      >
        <Button
          label='Discard'
          intent='secondary'
          size='sm'
          onClick={onClose}
          disabled={isSubmitting}
          startIcon={<CloseIcon />}
          className={!isDesktop ? 'w-full' : ''}
          type='button'
        />
        <Button
          label={isSubmitting ? 'Saving...' : 'Confirm'}
          intent='primary'
          size='sm'
          type='submit'
          disabled={isSubmitting || !isValid}
          className={!isDesktop ? 'w-full' : ''}
        />
      </div>
    </form>
  );
}
